import { CommonModule } from '@angular/common';
import { Component, Host, Inject, NgModule, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { AuthService } from '@app/services/auth.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { PowerBIService } from '@app/services/power-bi.service';
import { Environment } from '@environments/interface.environment';
import { PowerBIFrame } from '@app/models/power-bi-frame';
import { Observable, Subscription, BehaviorSubject, combineLatest, of } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Constants } from '@app/common/constants';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { ActionButtonModule } from '@app/components/actions/action-button/action-button.component';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { UserChannelTerm } from '@app/models/user-channel-term';
import { PowerBIFrameModule } from '@app/components/power-bi/power-bi-frame/power-bi-frame.component';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { LicenseService } from '@app/services/license.service';
import { O365Licenses } from '@app/common/o365-licenses';

@Component({
  selector: 'app-power-bi',
  templateUrl: './power-bi.component.html',
  styleUrls: ['./power-bi.component.scss'],
})
export class PowerBIComponent implements OnInit, OnDestroy {

  private changeSubject = new BehaviorSubject<PowerBIFrame>(null);
  private subscription: Subscription = new Subscription();
  userAccessToken$: Observable<string>;
  userChannelTerms$: Observable<Array<UserChannelTerm>>;
  selectedFrame$: Observable<PowerBIFrame>;
  powerBIFrames$: Observable<PowerBIFrame[]>;

  selectedFrame: PowerBIFrame;
  showPowerBIComponent = this.featureStore.state.showPowerBIComponent;

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private featureStore: FeatureSettingsStoreService,
              private powerBIService: PowerBIService,
              private userChannelTermsService: UserChannelTermsService,
              private authService: AuthService,
              private activatedRoute: ActivatedRoute,
              private licenseService: LicenseService,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective) { }

  handleSelection(event: MatSelectChange) {
    this.getAccessToken();
    this.changeSubject.next(event.value);
  }

  getAccessToken() {
    const scopes = this.authService.getScopesForEndpoint(Constants.POWERBI_API_ENDPOINT);
    this.userAccessToken$ = this.authService
      .acquireTokenSilentWithFallBackToPopup({ ...scopes, ...{ account: this.authService.accountInfo } }, scopes)
      .pipe(
        map(authResult => {
          return authResult.accessToken;
        })
      );
  }

  ngOnInit() {
    if (this.showPowerBIComponent) {
      this.getAccessToken();

      this.selectedFrame$ = this.changeSubject
        .pipe(
          filter(frame => !!frame),
          map(frame => {
            return frame;
          })
        );

      const activatedRouteForPrimaryOutlet = this.activatedRoute.root.children.find(_ => _.outlet === 'primary');

      const userChannelTerms$ = this.userChannelTermsService.getUserChannelTerms();

      const allPowerBIFrames$ = this.powerBIService.getAllPowerBIFrames();

      this.powerBIFrames$ = this.licenseService.hasUserLicense([O365Licenses.POWER_BI_FOR_OFFICE_365_ADD_ON, O365Licenses.POWER_BI_FREE, O365Licenses.POWER_BI_PRO])
        .pipe(
          mergeMap(hasLicense => {
            if (hasLicense) {
              return combineLatest([allPowerBIFrames$, userChannelTerms$, activatedRouteForPrimaryOutlet.url])
                .pipe(
                  map(([frames, userChannels, url]) => {
                    frames = frames.filter(frame => {
                      if (('/' + url.join('/')) !== frame.RouteId || frame.Active === false) {
                        return false;
                      }
                      const channelTerms = frame.ChannelTermPowerBIFrames;
                      if (channelTerms && channelTerms.length > 0) {
                        return channelTerms.some(c => userChannels.some(uc => uc.ChannelTermId === c.ChannelTermId));
                      } else {
                        return true;
                      }
                    });
                    if (frames && frames.length > 0) {
                      this.selectedFrame = frames[0];
                      this.changeSubject.next(this.selectedFrame);
                      this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
                    } else {
                      this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
                    }
                    return frames;
                  }));
            } else {
              this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
              return of([]);
            }
          })
        );
    } else {
      this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
    }

  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }
}
@NgModule({
  declarations: [PowerBIComponent],
  exports: [PowerBIComponent],
  imports: [
    CommonModule,
    RouterModule,
    ErrorTemplatesModule,
    ActionLinkModule,
    ActionButtonModule,
    MatSelectModule,
    PowerBIFrameModule
  ]
})

export class PowerBIModule { }
